import { useMemo } from 'react';
import { useLinkStore } from '@/stores/linkStore';
import { RelationshipBadge } from './RelationshipBadge';
import { useEntityNameResolver } from '@/hooks/useEntityNameResolver';
import type { EntityType } from '@/types/link';

interface EntityLinkedPreviewProps {
  entityType: EntityType;
  entityId: string;
  maxDisplay?: number;
}

export function EntityLinkedPreview({ entityType, entityId, maxDisplay = 3 }: EntityLinkedPreviewProps) {
  const { links } = useLinkStore();
  const { resolveEntityName } = useEntityNameResolver();

  const linkedEntities = useMemo(() => {
    return links
      .filter(link => 
        (link.source_type === entityType && link.source_id === entityId) ||
        (link.target_type === entityType && link.target_id === entityId)
      )
      .map(link => {
        const isSource = link.source_type === entityType;
        const type = (isSource ? link.target_type : link.source_type) as EntityType;
        const id = isSource ? link.target_id : link.source_id;
        return {
          id,
          type,
          name: resolveEntityName(type, id),
        };
      });
  }, [links, entityType, entityId, resolveEntityName]);

  if (linkedEntities.length === 0) return null;

  const displayEntities = linkedEntities.slice(0, maxDisplay);
  const remaining = linkedEntities.length - maxDisplay;

  return (
    <div className="flex flex-wrap gap-1 items-center">
      <span className="text-xs text-muted-foreground mr-1">Linked:</span>
      {displayEntities.map(entity => (
        <RelationshipBadge
          key={`${entity.type}-${entity.id}`}
          entityType={entity.type}
          entityId={entity.id}
          entityName={entity.name}
        />
      ))}
      {remaining > 0 && (
        <span className="text-xs text-muted-foreground">+{remaining} more</span>
      )}
    </div>
  );
}
